import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-na-godovshchinu")!;

const faqItems = [
  { q: "На какую годовщину можно заказать песню?", a: "На любую: годовщина свадьбы, отношений, знакомства. Один год или тридцать — бот подстроит текст." },
  { q: "Что написать, чтобы песня получилась личной?", a: "Расскажите вашу историю: как познакомились, какие моменты запомнились, какие общие шутки и привычки." },
  { q: "Можно ли подарить песню на годовщину свадьбы родителям?", a: "Конечно. Это один из самых трогательных подарков." },
  { q: "Успею ли я, если годовщина завтра?", a: "Да. Демоверсия готовится за 3 минуты." },
  { q: "Сколько стоит песня на годовщину?", a: "Демоверсия бесплатна. Полная версия — от 249 ₽." },
];

const PesnyaNaGodovshchinu = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">Песня на годовщину на заказ</h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Ресторан забронирован. Она сидит напротив. И из динамика звучит <strong className="text-foreground">песня, в которой — вся ваша история</strong>.</p>
            <p><strong className="text-foreground">Персональная песня на годовщину</strong> — это три минуты вашей совместной жизни, переложенные на музыку.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему песня — идеальный подарок на годовщину</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Годовщина — день, когда вы отмечаете путь. Песня остаётся в плейлисте — и каждый раз возвращает в тот момент.</p>
              <p>Подробнее о свадебных песнях — <Link to="/pesnya-na-svadbu" className="text-primary hover:underline">песня на свадьбу</Link>.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">💕 Создать песню на годовщину</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Готово за 3 минуты.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Как вручить песню так, чтобы запомнилось навсегда</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground">Романтический ужин.</strong> Пусть музыка скажет за вас.</p>
              <p><strong className="text-foreground">Утреннее сообщение.</strong> Отправьте ссылку на подарочную страницу.</p>
              <p><strong className="text-foreground">На семейном празднике.</strong> О подарке <Link to="/pesnya-mame-v-podarok" className="text-primary hover:underline">маме</Link> — отдельная страница.</p>
              <p><strong className="text-foreground">В путешествии.</strong> Включите песню в том городе, где всё началось.</p>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Мы слушали и молчали. А потом она сказала: давай включим ещё раз»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Муж готовил подарок на пятую годовщину. Выбрал акустическую балладу. Они слушали три раза подряд. И каждый раз находили новую деталь, которая попала точно в сердце.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Пусть ваша история зазвучит</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Частые вопросы о песнях на годовщину</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Создайте саундтрек вашей любви</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Песня, которая расскажет историю лучше тысячи слов.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaNaGodovshchinu;
